import { buttonVariants } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { ArrowRightIcon } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import type { ComponentProps, PropsWithChildren } from 'react'

import { stripLocaleFromPath } from '~/i18n/routing'
import { SectionTitle } from '~/domains/home/components/section-title'
import { SectionWrapper } from '~/domains/home/components/section-wrapper'

const SolutionTitle = ({ children }: PropsWithChildren) => (
	<h3 className='font-bold text-2xl leading-none'>{children}</h3>
)

const SolutionBody = ({ children }: PropsWithChildren) => (
	<p className='mt-auto flex-1 text-sm'>{children}</p>
)

const SolutionButton = ({
	children,
	href,
	target,
	...props
}: ComponentProps<typeof Link>) => (
	<Link
		className={buttonVariants({
			className: 'ml-auto',
			size: 'sm',
			variant: 'textPrimary',
		})}
		href={href}
		target={target}
		{...props}
	>
		{children} <ArrowRightIcon className='ml-1' />
	</Link>
)

export const OnDemand = ({ className }: { className?: string }) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { t } = useLingui()

	return (
		<Card className={cn('flex flex-col gap-2 p-6 md:w-1/3', className)}>
			<SolutionTitle>{t`On demand`}</SolutionTitle>
			<SolutionBody>{t`Coaching, rendez-vous business, journée Offsite, COMEX, CODIR, COPIL, formation, journée d'étude...`}</SolutionBody>
			<SolutionButton
				data-umami-event='click-offers-on-demand'
				data-umami-event-source={pathname}
				href='/paris'
			>
				{t`Je réserve maintenant`}
			</SolutionButton>
		</Card>
	)
}

export const OnDemandBusiness = ({ className }: { className?: string }) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { t } = useLingui()

	return (
		<Card className={cn('flex flex-col gap-2 p-6 md:w-1/3', className)}>
			<SolutionTitle>{t`On demand - Entreprise`}</SolutionTitle>
			<SolutionBody>{t`Une seule facture, gestion de droits et budget, synchronisation agenda, lien de réservation, base de connaissance meeting...`}</SolutionBody>

			<SolutionButton
				data-umami-event='click-offers-on-demand-business'
				data-umami-event-source={pathname}
				href='https://meet.brevo.com/cocoon-space-sylvain-30/on-demand-entreprise'
				target='_blank'
			>
				{t`Je prends rendez-vous`}
			</SolutionButton>
		</Card>
	)
}

export const MonthlyDays = ({ className }: { className?: string }) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { t } = useLingui()

	return (
		<Card className={cn('flex flex-col gap-2 p-6 md:w-1/3', className)}>
			<SolutionTitle>
				<Trans>
					Location mensuelle <br />
					<span className='text-base text-muted-foreground'>jours fixes</span>
				</Trans>
			</SolutionTitle>

			<SolutionBody>
				{t`1 à 3 jours par semaine, un bureau pour votre entreprise ou votre équipe.`}
			</SolutionBody>

			<SolutionButton
				data-umami-event='click-offers-monthly-days'
				data-umami-event-source={pathname}
				href='https://cocoonspace.typeform.com/to/updKKFB7'
				target='_blank'
			>
				{t`Je souhaite un devis`}
			</SolutionButton>
		</Card>
	)
}

export const MonthlyDaysPack = ({ className }: { className?: string }) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { t } = useLingui()

	return (
		<Card className={cn('flex flex-col gap-2 p-6 md:w-1/3', className)}>
			<SolutionTitle>
				<Trans>
					Location mensuelle <br />
					<span className='text-base text-muted-foreground'>pack jours</span>
				</Trans>
			</SolutionTitle>
			<SolutionBody>{t`5 à 10 jours par mois, réunissez votre équipe et tout en leur laissant de l'autonomie.`}</SolutionBody>

			<SolutionButton
				data-umami-event='click-offers-monthly-days-pack'
				data-umami-event-source={pathname}
				href='https://cocoonspace.typeform.com/to/updKKFB7'
				target='_blank'
			>
				{t`Je souhaite un devis`}
			</SolutionButton>
		</Card>
	)
}

export const PrivateEvents = ({ className }: { className?: string }) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { t } = useLingui()

	return (
		<Card className={cn('flex flex-col gap-2 p-6 md:w-1/3', className)}>
			<SolutionTitle>{t`Privatisation & évènements dédiés`}</SolutionTitle>
			<SolutionBody>{t`Organisation d'un évènement dans 1 ou plusieurs Cocoons et/ou sur 1 ou plusieurs jours.`}</SolutionBody>

			<SolutionButton
				data-umami-event='click-offers-private-events'
				data-umami-event-source={pathname}
				href='https://cocoonspace.typeform.com/to/ekCM8SS9#page=HP'
				target='_blank'
			>
				{t`Je souhaite être accompagné`}
			</SolutionButton>
		</Card>
	)
}

export const Solutions = () => {
	const { t } = useLingui()

	return (
		<div>
			<SectionWrapper>
				<SectionTitle>{t`Une solution pour chaque besoin`}</SectionTitle>

				<div className='flex flex-col flex-wrap justify-center gap-6 md:flex-row'>
					<OnDemand />
					<OnDemandBusiness />
					<MonthlyDays />
					<MonthlyDaysPack />
					<PrivateEvents />
				</div>
			</SectionWrapper>
		</div>
	)
}
